import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../../components";
import EditIcon from "../../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../../service/ApiService";
import { DatabaseEntities } from "../../../model/enum/database-entities.enum";
import AlertService from "../../../service/AlertService";
import ToastMessages from "../../../model/enum/toast-messages.enum";
import TagGroup from "../../../model/tag-group";
import PageResultResponse from "../../../model/page-result-response";
import DeleteModal from "../../modal/DeleteModal";
import Select, { StylesConfig } from "react-select";
import Tag from "../../../model/tag";
import Misc from "../../../utils/Misc";
import useStyles from "./styles";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "95% !important",
    borderColor: DarkGrey,
    borderRadius: "5px",
    marginLeft: "10px",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
        ? PrimaryColor
        : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

interface Props {
  tagGroup?: TagGroup;
  resetList?: () => void;
}

const TagGroupModal = ({ tagGroup, resetList }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = tagGroup != null;
  const id = edit ? tagGroup?.id : 0;
  const [name, setName] = useState(edit ? tagGroup?.name : "");
  const [description, setDescription] = useState(
    edit ? tagGroup?.description : "",
  );
  const [color, setColor] = useState(edit ? tagGroup?.color : "#CCCCCC");
  const [tags, setTags] = useState<Tag[]>([]);
  const [items, setItems] = useState<Tag[]>(
    edit ? tagGroup?.TagGroupItems?.map((item) => item.Tag) || [] : [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<PageResultResponse>(DatabaseEntities.TAGS).then(
        (data) => {
          if (Misc.isArrayEmpty(data.items)) return;
          setTags(data.items);
        },
      );
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(tags)) fetchData();
  }, [tags]);

  const handleName = (e: any) => setName(e.target.value);
  const handleDescription = (e: any) => setDescription(e.target.value);
  const handleColor = (e: any) => setColor(e.target.value);
  const handleAddTag = (e: any) => setItems(e);

  const handleCreate = async (payload: TagGroup) => {
    await ApiService.post<TagGroup>(DatabaseEntities.TAG_GROUPS, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList?.();
        setName("");
        setDescription("");
        setColor("#CCCCCC");
        setItems([]);
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
      });
  };

  const handleUpdate = async (payload: TagGroup) => {
    await ApiService.put<TagGroup>(
      `${DatabaseEntities.TAG_GROUPS}/${payload.id}`,
      payload,
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: TagGroup) => {
    await ApiService.delete(`${DatabaseEntities.TAG_GROUPS}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = (): boolean => {
    return Misc.isNullOrEmpty(name);
  };

  const handleSubmit = async (e: any) => {
    if (invalidInput()) return;

    setLoading(true);
    const payload: TagGroup = {
      id: id,
      name: name,
      description: description,
      color: color,
      tags: items,
    };

    if (edit) await handleUpdate(payload);
    else await handleCreate(payload);

    setLoading(false);
    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Tag Group
        </Btn>
      )}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Box sx={style}>
              <div className={classes.headerDiv}>
                {edit ? (
                  <Typography className={classes.title}>
                    <DeleteModal item={tagGroup} handleDelete={handleDelete} />
                  </Typography>
                ) : (
                  <Typography className={classes.title}>
                    Create Tag Group
                  </Typography>
                )}
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
              <hr />
              <div className={classes.form}>
                <div className={classes.inputGroup}>
                  <label className={classes.label}>Name</label>
                  <input
                    type="text"
                    className={classes.input}
                    value={name}
                    onChange={handleName}
                    placeholder="Group"
                  />
                </div>
                <div className={classes.inputGroup}>
                  <label className={classes.label}>Description</label>
                  <input
                    type="text"
                    className={classes.input}
                    value={description}
                    onChange={handleDescription}
                    placeholder="Group description"
                  />
                </div>
                <div className={classes.inputGroup}>
                  <label className={classes.label}>Color</label>
                  <input
                    type="color"
                    className={classes.input}
                    value={color}
                    onChange={handleColor}
                  />
                </div>
                <div className={classes.inputGroup}>
                  <label className={classes.label}>Tags</label>
                  <Select
                    isMulti
                    options={tags}
                    getOptionLabel={(e) => e.name}
                    getOptionValue={(e) => e.id.toString()}
                    value={items}
                    onChange={handleAddTag}
                    styles={styles}
                    placeholder="Select tags..."
                  />
                </div>
              </div>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={handleSubmit}
                  disabled={invalidInput()}
                >
                  {edit ? "Save" : "Create"}
                </Btn>
              </div>
            </Box>
          )}
        </Modal>
      )}
    </div>
  );
};

export default TagGroupModal;
